// CreateOrderModal.jsx
import React, { useEffect, useState, useContext, useCallback, useRef, useMemo } from 'react';
import Swal from 'sweetalert2';
import axios from 'axios';
import {
  PlusCircle,
  Image as ImageIcon,
  Tags,
  DollarSign,
  Info,
  X,
  CheckCircle2,
  Beef,
  Edit3,
  Layers as VariantIcon,
  Pencil,
  Trash2,
  Plus
} from 'lucide-react';
import './InventoryAccordion.css';
import MyContext from "../../../MyContext";

// ---- helpers for dish/icon normalization ----
const dishToPayload = (dishLabel) => {
  if (/^veg$/i.test(dishLabel)) return '["1"]';
  if (/non[- ]?veg/i.test(dishLabel)) return '["2"]';
  if (/egg/i.test(dishLabel)) return '["3"]';
  return '';
};
const iconForDish = (dishLabel, existing) => {
  if (existing) return existing;
  if (/^veg$/i.test(dishLabel)) return "veg";
  if (/non[- ]?veg/i.test(dishLabel)) return "nonveg";
  if (/egg/i.test(dishLabel)) return "egg";
  return "";
};

const AddItemModal = ({
  show,
  onClose,
  userID,
  baseUrl,
  BASIC_AUTH,

  // cascading selects
  categories = [],
  subcategories = [],
  initialCategory = "",
  loadSubcategories,

  // mode / seed
  mode = "add",                // "add" | "edit"
  seedFrom = null,             // object that maps to form fields
  onSaved = null,              // callback after success
}) => {
  const { config } = useContext(MyContext);
  const isEdit = mode === "edit";

  // Size options (extended)
  const SIZE_OPTIONS = useMemo(
    () => [
      "",
      "Small","Medium","Large","Extra Large",
      "Quarter","Half","Three-Quarter","Full",
      "100g","200g","250g","400g","500g","750g","1kg","2kg","5kg","10kg",
      "100ml","200ml","250ml","500ml","750ml","1L","2L","5L",
      "1 Piece","2 Pieces","3 Pieces","Pack of 6","Pack of 12","Single","Double","Family Pack","Combo",
    ], []
  );

  // Variant category groups for the inline manager
  const VARIANT_CATEGORY_GROUPS = useMemo(() => [
    {
      label: "Industry",
      options: [
        "Food","Bakery","Grocery","Dairy","Beverages","Household","Personal Care","Pharmacy",
        "Pet Care","Baby Care","Stationery","Home & Kitchen","Cleaning","Electronics","Fashion","Health & Wellness",
      ],
    },
    {
      label: "Food Types",
      options: [
        "Fruits & Vegetables","Staples & Grains","Rice & Flour","Pulses & Lentils","Oils & Ghee","Masala & Spices",
        "Snacks & Namkeen","Biscuits & Cookies","Chocolates & Sweets","Breakfast & Cereals","Instant & Ready-to-Eat",
        "Bakery & Breads","Cakes & Pastries","Dairy & Cheese","Paneer & Tofu","Eggs & Meat","Seafood","Frozen Foods",
        "Beverages - Soft Drinks","Beverages - Juices","Tea & Coffee","Water & Ice",
      ],
    },
  ], []);

  // Options
  const [catOptions, setCatOptions] = useState([]);
  const [subcatOptions, setSubcatOptions] = useState([]);

  // Form (aligned with /api/Order/v3.0/orderInsertCatalogItem + barcodeNumber + variant)
  const [form, setForm] = useState({
    itemId: '',
    catId: '',
    sizeId: '',
    status: 'Y',
    itemName: '',
    itemDescription: '',
    itemNameTrans: '',
    itemDescriptionTrans: '',
    category: '',
    subCategory: '',
    categoryDescription: '[]',
    position: '',
    dish: '',
    iconDish: '',
    photo: '',
    ratings: '',
    price: '',
    discount: '',
    discountPrice: '',
    size: '',
    sizeTrans: '',
    customizations: '[]',
    barcodeNumber: '',
    // NEW for Variant in this modal too:
    variantId: '',
    variantTitle: '',
  });

  const [manualDiscountPrice, setManualDiscountPrice] = useState(false);
  const [preview, setPreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const seededRef = useRef(false);

  // ----- VARIANT state (CRUD manager) -----
  const [variants, setVariants] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [showVariantManager, setShowVariantManager] = useState(false);
  const [variantForm, setVariantForm] = useState({
    variantId: "",
    variantTitle: "",
    category: initialCategory || "",
  });

  // ---------- API helpers (plain functions; not hooks) ----------
  const postJson = async (url, payload) => {
    if (BASIC_AUTH) {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Basic ${BASIC_AUTH}` },
        body: JSON.stringify(payload),
      });
      return res.json();
    }
    const res = await axios.post(url, payload, config);
    return res.data;
  };

  const fetchCatsInternal = useCallback(async () => {
    try {
      const res = await axios.post(
        `${baseUrl}/api/Inventory/v1.0/getInventoryCategories`,
        { userid: userID },
        config
      );
      if (res.data?.responseCode === "00") {
        const list = (res.data.response || []).map(r => ({
          label: r.category,
          value: r.category,
          catId: r.catId || ''
        }));
        return list;
      }
    } catch (e) {
      console.error("getInventoryCategories error:", e);
    }
    return [];
  }, [baseUrl, userID, config]);

  const fetchSubsInternal = useCallback(async (cat) => {
    if (!cat) return [];
    try {
      const res = await axios.post(
        `${baseUrl}/api/Inventory/v1.0/getInventorySubCategories`,
        { userid: userID, category: cat },
        config
      );
      if (res.data?.responseCode === "00") {
        const list = (res.data.response || []).map(r => ({
          label: r.subCategory || r.sub_category || r.category,
          value: r.subCategory || r.sub_category || r.category
        }));
        return list;
      }
    } catch (e) {
      console.error("getInventorySubCategories error:", e);
    }
    return [];
  }, [baseUrl, userID, config]);

  const getCategories = useCallback(async () => {
    if (Array.isArray(categories) && categories.length) return categories;
    return await fetchCatsInternal();
  }, [categories, fetchCatsInternal]);

  const getSubcategories = useCallback(async (cat) => {
    if (Array.isArray(subcategories) && subcategories.length) return subcategories;
    if (typeof loadSubcategories === 'function') return await loadSubcategories(cat);
    return await fetchSubsInternal(cat);
  }, [subcategories, loadSubcategories, fetchSubsInternal]);

  // ---------- Side effects (all unconditionally declared) ----------
  // Mirrors
  useEffect(() => { setForm(f => ({ ...f, itemNameTrans: f.itemName })); }, [form.itemName]);
  useEffect(() => { setForm(f => ({ ...f, itemDescriptionTrans: f.itemDescription })); }, [form.itemDescription]);
  useEffect(() => { setForm(f => ({ ...f, sizeTrans: f.size })); }, [form.size]);

  // Auto-calc discount price when not manually overridden
  useEffect(() => {
    if (!manualDiscountPrice) {
      const p = Number(form.price) || 0;
      const d = Number(form.discount) || 0;
      const calc = Math.max(p - (p * d) / 100, 0);
      setForm(f => ({ ...f, discountPrice: Number.isFinite(calc) ? String(calc.toFixed(2)) : '0' }));
    }
  }, [form.price, form.discount, manualDiscountPrice]);

  // Preview from base64 only
  useEffect(() => {
    if (!show) return; // early exit inside the effect is fine
    setPreview(form.photo && form.photo.startsWith('data:') ? form.photo : '');
  }, [show, form.photo]);

  // Seed on open (keep hook unconditional; branch inside)
  useEffect(() => {
    // z-index fix for SweetAlert (always inject/remove; harmless if already present)
    const s = document.createElement("style");
    s.setAttribute("data-swal-zfix", "true");
    s.innerHTML = `.swal2-container{z-index:100000!important}`;
    document.head.appendChild(s);

    const run = async () => {
      if (!show) { // effect still runs, but we bail logically
        seededRef.current = false;
        return;
      }

      const cats = await getCategories();
      setCatOptions(cats);

      let chosenCategoryName = initialCategory || (cats?.[0]?.label || "");
      let matchedCatId = "";

      if (isEdit && seedFrom && !seededRef.current) {
        const seed = { ...seedFrom };
        if (seed.category) chosenCategoryName = seed.category;
        const matched = cats?.find(c => c.label === chosenCategoryName) || null;
        matchedCatId = matched?.catId || "";

        const subs = await getSubcategories(chosenCategoryName);
        setSubcatOptions(subs);

        setForm((f) => ({
          ...f,
          ...seed,
          catId: seed.catId || matchedCatId || "",
          subCategory: seed.subCategory || (subs?.[0]?.value || ""),
          barcodeNumber: seed.barcodeNumber || seed.barcode || f.barcodeNumber || "",
          variantId: seed.variantId || "",
          variantTitle: seed.variantTitle || "",
        }));

        setVariantForm(vf => ({ ...vf, category: seed.category || chosenCategoryName || "" }));
        seededRef.current = true;
        return;
      }

      if (!isEdit && !seededRef.current) {
        const matched = cats?.find(c => c.label === chosenCategoryName) || null;
        matchedCatId = matched?.catId || "";

        const subs = await getSubcategories(chosenCategoryName);
        setSubcatOptions(subs);

        setForm(f => ({
          ...f,
          category: f.category || chosenCategoryName,
          catId: f.catId || (typeof matchedCatId === 'string' ? matchedCatId : ''),
          subCategory: f.subCategory || (subs?.[0]?.value || ''),
          barcodeNumber: f.barcodeNumber || "",
          variantId: f.variantId || "",
          variantTitle: f.variantTitle || "",
        }));

        setVariantForm(vf => ({ ...vf, category: chosenCategoryName || "" }));
        seededRef.current = true;
      }
    };

    run();

    return () => {
      const el = document.querySelector('style[data-swal-zfix="true"]');
      if (el) el.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, isEdit, seedFrom, initialCategory, getCategories, getSubcategories]);

  // Fetch variants whenever modal opens (hook always declared)
  const fetchVariants = useCallback(async () => {
    try {
      setLoadingVariants(true);
      const body = { userid: String(userID ?? "") };
      const data = await postJson(`${baseUrl}/api/Inventory/v1.0/getAllInventoryVariant`, body);

      // PREDICTED getAllVariant list (adjust if keys differ):
      // A) { responseCode:"00", response:[{ pk_variant_id, variant_title, category }]}
      // B) { code:200, data:[{ _id, variantTitle, category }]}
      let list = [];
      if (Array.isArray(data?.response)) {
        list = data.response.map((r) => ({
          id: String(r.pk_variant_id ?? r.variantId ?? r._id ?? ""),
          variant_title: r.variant_title ?? r.variantTitle ?? "",
          category: r.category ?? "",
        }));
      } else if (Array.isArray(data?.data)) {
        list = data.data.map((r) => ({
          id: String(r._id ?? r.variantId ?? r.pk_variant_id ?? ""),
          variant_title: r.variantTitle ?? r.variant_title ?? "",
          category: r.category ?? "",
        }));
      }
      setVariants(list);
    } catch {
      setVariants([]);
    } finally {
      setLoadingVariants(false);
    }
  }, [baseUrl, userID]); // postJson is stable enough as plain function

  useEffect(() => {
    if (!show) return; // allowed: early exit inside effect
    fetchVariants();
  }, [show, fetchVariants]);

  // ---------- handlers ----------
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "variantId") {
      const sel = variants.find(v => String(v.id) === String(value));
      setForm(f => ({ ...f, variantId: value, variantTitle: sel?.variant_title || "" }));
      return;
    }
    const stringFields = ['ratings', 'price', 'discount', 'discountPrice', 'position'];
    setForm(f => ({
      ...f,
      [name]: stringFields.includes(name)
        ? (value === '' ? '' : String(value))
        : value
    }));
  };

  const handleDiscountPriceChange = (e) => {
    setManualDiscountPrice(true);
    handleChange(e);
  };

  const handleFileToBase64 = async (file) => {
    if (!file) return;
    const toBase64 = (f) =>
      new Promise((res, rej) => {
        const reader = new FileReader();
        reader.readAsDataURL(f);
        reader.onload = () => res(reader.result);
        reader.onerror = (err) => rej(err);
      });
    try {
      const b64 = await toBase64(file);
      setForm(f => ({ ...f, photo: b64 }));
      setPreview(b64);
    } catch (err) {
      console.error('Image encode error:', err);
      Swal.fire({ icon: 'error', title: 'Image Error', text: 'Failed to read image file.' });
    }
  };

  const uploadImageIfNeeded = async () => {
    if (form.photo && !form.photo.startsWith('data:')) return form.photo || '';
    if (!form.photo || !form.photo.startsWith('data:')) return '';
    const requestBody = { userid: userID, item_name: form.itemName || 'Item', type: 'IMG', image_data: form.photo };
    const resp = await axios.post('https://bumppy.in/anlook/api/v1/uploadItemImage.php', requestBody, config);
    if (resp?.data?.status === '0' && resp?.data?.image_url) return resp.data.image_url;
    throw new Error(resp?.data?.message || 'Image upload failed');
  };

  const onCategoryChange = async (e) => {
    const catLabel = e.target.value;
    const matched = catOptions.find(c => c.label === catLabel);
    setForm(f => ({ ...f, category: catLabel, catId: matched?.catId || '', subCategory: "" }));
    const list = typeof loadSubcategories === 'function'
      ? await loadSubcategories(catLabel)
      : await getSubcategories(catLabel);
    setSubcatOptions(list);
    setForm(f => ({ ...f, subCategory: list?.[0]?.value || "" }));
    // default variant category selector to this category
    setVariantForm(vf => ({ ...vf, category: catLabel || "" }));
  };

  const createVariant = async () => {
    if (!variantForm.variantTitle.trim()) {
      Swal.fire({ icon: "warning", title: "Variant title required" });
      return;
    }
    const body = {
      variantTitle: variantForm.variantTitle.trim(),
      category: variantForm.category?.trim() || "",
    };
    try {
      const data = await postJson(`${baseUrl}/api/Inventory/v1.0/insertInventoryVariant`, body);
      if (data?.responseCode === "00" || data?.code === 200) {
        Swal.fire({ icon: "success", title: "Variant added" });
        setVariantForm({ variantId: "", variantTitle: "", category: form.category || "" });
        fetchVariants();
      } else {
        Swal.fire({ icon: "error", title: "Failed", text: data?.responseMessage || "Could not add variant." });
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err?.message || "Could not add variant." });
    }
  };

  const startEditVariant = (v) => {
    setVariantForm({
      variantId: String(v.id),
      variantTitle: v.variant_title || "",
      category: v.category || form.category || "",
    });
  };

  const updateVariant = async () => {
    if (!variantForm.variantId) return;
    if (!variantForm.variantTitle.trim()) {
      Swal.fire({ icon: "warning", title: "Variant title required" });
      return;
    }
    const body = {
      variantId: String(variantForm.variantId),
      variantTitle: variantForm.variantTitle.trim(),
      category: variantForm.category?.trim() || "",
    };
    try {
      const data = await postJson(`${baseUrl}/api/Inventory/v1.0/updateInventoryVariant`, body);
      if (data?.responseCode === "00" || data?.code === 200) {
        Swal.fire({ icon: "success", title: "Variant updated" });
        setVariantForm({ variantId: "", variantTitle: "", category: form.category || "" });
        fetchVariants();
      } else {
        Swal.fire({ icon: "error", title: "Failed", text: data?.responseMessage || "Could not update variant." });
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err?.message || "Could not update variant." });
    }
  };

  const deleteVariant = async (id) => {
    const ok = await Swal.fire({
      icon: "warning",
      title: "Delete variant?",
      text: "This cannot be undone.",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!ok.isConfirmed) return;

    const body = { variantId: String(id) };
    try {
      const data = await postJson(`${baseUrl}/api/Inventory/v1.0/deleteInventoryVariant`, body);
      if (data?.responseCode === "00" || data?.code === 200) {
        Swal.fire({ icon: "success", title: "Variant deleted" });
        if (String(form.variantId) === String(id)) setForm(f => ({ ...f, variantId: "", variantTitle: "" }));
        fetchVariants();
      } else {
        Swal.fire({ icon: "error", title: "Failed", text: data?.responseMessage || "Could not delete variant." });
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err?.message || "Could not delete variant." });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // Validations
    if (!form.itemName || !form.price || !form.category) {
      Swal.fire({ icon: 'warning', title: 'Missing fields', text: 'Item Name, Price and Category are required.' });
      return;
    }
    if (!['Y','N'].includes(form.status)) {
      Swal.fire({ icon: 'warning', title: 'Invalid Status', text: 'Status must be Active (Y) or Inactive (N).' });
      return;
    }
    if (isEdit && !form.itemId) {
      Swal.fire({ icon: 'warning', title: 'Missing Item ID', text: 'itemId is required for update.' });
      return;
    }

    try {
      setSubmitting(true);

      const photoUrl = await uploadImageIfNeeded();
      const dishPayload = dishToPayload(form.dish);
      const iconDishPayload = iconForDish(form.dish, form.iconDish);

      // Payload for /api/Order/v3.0/orderInsertCatalogItem
      const payload = {
        userid: String(userID),
        itemName: String(form.itemName || ''),
        itemId: String(form.itemId || ''),              // empty for add if backend supports auto-id
        status: String(form.status || 'Y'),
        itemDescription: String(form.itemDescription || ''),
        dish: String(dishPayload || ''),                // '["1"]' | '["2"]' | '["3"]' | ''
        photo: String(photoUrl || form.photo || ''),
        itemNameTrans: String(form.itemNameTrans || ''),
        itemDescriptionTrans: String(form.itemDescriptionTrans || ''),
        category: String(form.category || ''),
        ratings: String(form.ratings || ''),
        price: String(form.price || ''),
        discount: String(form.discount || ''),
        discountPrice: String(form.discountPrice || ''),
        size: String(form.size || ''),
        sizeId: String(form.sizeId || ''),
        sizeTrans: String(form.sizeTrans || ''),
        iconDish: String(iconDishPayload || ''),
        position: String(form.position || ''),
        catId: String(form.catId || ''),
        subCategory: String(form.subCategory || ''),
        categoryDescription: String(form.categoryDescription || '[]'),
        customizations: String(form.customizations || '[]'),
        barcodeNumber: String(form.barcodeNumber || ''),
        // Include variant if backend supports it (remove if rejected):
        variantId: String(form.variantId || ''),
        variantTitle: String(form.variantTitle || ''),
      };

      const res = await fetch(`${baseUrl}/api/Order/v3.0/orderInsertCatalogItem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${BASIC_AUTH}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data?.responseCode === '00') {
        Swal.fire({
          icon: 'success',
          title: isEdit ? 'Item Updated' : 'Item Saved',
          text: data?.responseMessage || (isEdit ? 'Catalog item updated successfully.' : 'Catalog item saved successfully.'),
          confirmButtonColor: '#2F7D32'
        });

        onSaved?.();
        if (!isEdit) {
          setForm({
            itemId: '',
            catId: '',
            sizeId: '',
            status: 'Y',
            itemName: '',
            itemDescription: '',
            itemNameTrans: '',
            itemDescriptionTrans: '',
            category: '',
            subCategory: '',
            categoryDescription: '[]',
            position: '',
            dish: '',
            iconDish: '',
            photo: '',
            ratings: '',
            price: '',
            discount: '',
            discountPrice: '',
            size: '',
            sizeTrans: '',
            customizations: '[]',
            barcodeNumber: '',
            variantId: '',
            variantTitle: '',
          });
          setPreview('');
          setManualDiscountPrice(false);
        }
      } else {
        Swal.fire({ icon: 'error', title: 'Failed', text: data?.responseMessage || 'Something went wrong.' });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: 'Error', text: err?.message || 'Could not save item.' });
    } finally {
      setSubmitting(false);
    }
  };

  // ⚠️ Important: the conditional return is placed AFTER all hooks above
  if (!show) return null;

  return (
    <div className="inventory-modal-overlay">
      <div className="inventory-modal-box inv-itemadd-fixed-size">
        <div className="inventory-modal-header inv-itemadd-head-gradient">
          <div className="inv-itemadd-head-title">
            {isEdit ? <Edit3 size={20} /> : <PlusCircle size={20} />}
            <h4>{isEdit ? 'Update Catalog Item' : 'Add Catalog Item'}</h4>
          </div>
          <button className="inventory-close-btn inv-itemadd-icon-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="inventory-modal-body inv-itemadd-body-scroll">
          {/* BASIC */}
          <section className="inv-itemadd-section">
            <div className="inv-itemadd-section-title">
              <Info size={16} />
              <span>Basic Details</span>
            </div>
            <div className="inv-itemadd-grid-3">
              {isEdit && (
                <div className="inv-itemadd-field">
                  <label>Item ID</label>
                  <input type="text" name="itemId" value={form.itemId} onChange={e=>{}} readOnly />
                </div>
              )}

              <div className="inv-itemadd-field">
                <label>Item Name<span className="inv-itemadd-req">*</span></label>
                <input type="text" name="itemName" value={form.itemName} onChange={(e)=>setForm(f=>({...f,itemName:e.target.value}))} required />
              </div>

              <div className="inv-itemadd-field">
                <label>Barcode</label>
                <input
                  type="text"
                  name="barcodeNumber"
                  value={form.barcodeNumber}
                  onChange={(e)=>setForm(f=>({...f,barcodeNumber:e.target.value}))}
                  placeholder="Scan or type barcode"
                  inputMode="numeric"
                  autoComplete="off"
                />
              </div>

              <div className="inv-itemadd-field">
                <label>Active</label>
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.status === 'Y'}
                    onChange={(e) => setForm(f => ({ ...f, status: e.target.checked ? 'Y' : 'N' }))}
                  />
                  <span className="small text-muted">{form.status === 'Y' ? 'Y' : 'N'}</span>
                </div>
              </div>

              <div className="inv-itemadd-field inv-itemadd-col-span-3">
                <label>Description</label>
                <textarea name="itemDescription" value={form.itemDescription} onChange={(e)=>setForm(f=>({...f,itemDescription:e.target.value}))} rows={3} />
              </div>

              <div className="inv-itemadd-field">
                <label>Item Name (Trans)</label>
                <input type="text" name="itemNameTrans" value={form.itemNameTrans} onChange={(e)=>setForm(f=>({...f,itemNameTrans:e.target.value}))} />
              </div>

              <div className="inv-itemadd-field inv-itemadd-col-span-2">
                <label>Description (Trans)</label>
                <input type="text" name="itemDescriptionTrans" value={form.itemDescriptionTrans} onChange={(e)=>setForm(f=>({...f,itemDescriptionTrans:e.target.value}))} />
              </div>
            </div>
          </section>

          {/* CATEGORY, SIZE & VARIANT */}
          <section className="inv-itemadd-section">
            <div className="inv-itemadd-section-title">
              <Tags size={16} />
              <span>Category, Size & Variant</span>
            </div>
            <div className="inv-itemadd-grid-3">
              <div className="inv-itemadd-field">
                <label>Category<span className="inv-itemadd-req">*</span></label>
                <select name="category" value={form.category} onChange={onCategoryChange} required>
                  <option value="">Select category</option>
                  {catOptions.map(opt => (
                    <option key={opt.label} value={opt.label}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="inv-itemadd-field">
                <label>catId (optional)</label>
                <input type="text" name="catId" value={form.catId} onChange={(e)=>setForm(f=>({...f,catId:e.target.value}))} placeholder="Auto-filled when available" />
              </div>

              <div className="inv-itemadd-field">
                <label>Sub Category</label>
                <select name="subCategory" value={form.subCategory} onChange={(e)=>setForm(f=>({...f,subCategory:e.target.value}))} disabled={!subcatOptions.length}>
                  {subcatOptions.length === 0
                    ? <option value="">Select category first</option>
                    : subcatOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>

              {/* Size */}
              <div className="inv-itemadd-field">
                <label>Size</label>
                <select name="size" value={form.size} onChange={(e)=>setForm(f=>({...f,size:e.target.value}))}>
                  {SIZE_OPTIONS.map((s, idx) => (
                    <option key={idx} value={s}>{s || "Select"}</option>
                  ))}
                </select>
              </div>

              <div className="inv-itemadd-field">
                <label>Size ID (optional)</label>
                <input type="text" name="sizeId" value={form.sizeId} onChange={(e)=>setForm(f=>({...f,sizeId:e.target.value}))} />
              </div>

              <div className="inv-itemadd-field inv-itemadd-col-span-2">
                <label>Size (Trans)</label>
                <input type="text" name="sizeTrans" value={form.sizeTrans} onChange={(e)=>setForm(f=>({...f,sizeTrans:e.target.value}))} placeholder="Auto from Size" />
              </div>

              {/* Variant Select + Manager Toggle */}
              <div className="inv-itemadd-field">
                <label>Variant</label>
                <div className="d-flex" style={{ gap: 8 }}>
                  <select
                    name="variantId"
                    value={form.variantId}
                    onChange={handleChange}
                    className="flex-1"
                  >
                    <option value="">{loadingVariants ? "Loading…" : "Select"}</option>
                    {variants.map(v => (
                      <option key={v.id} value={v.id}>{v.variant_title}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="inv-itemadd-outline"
                    onClick={() => setShowVariantManager(s => !s)}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                  >
                    <Plus size={14} /> Add
                  </button>
                </div>
                {form.variantTitle ? <small className="text-muted">Selected: {form.variantTitle}</small> : null}
              </div>

              {/* Inline Variant Manager */}
              {showVariantManager && (
                <div className="inv-itemadd-col-span-3" style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#fafafa" }}>
                  <div className="inv-itemadd-section-title" style={{ marginBottom: 8 }}>
                    <VariantIcon size={16} />
                    <span>Variant Manager</span>
                  </div>

                  <div className="inv-itemadd-grid-3">
                    <div className="inv-itemadd-field">
                      <label>Variant Title</label>
                      <input
                        type="text"
                        value={variantForm.variantTitle}
                        onChange={(e) => setVariantForm(vf => ({ ...vf, variantTitle: e.target.value }))}
                        placeholder="e.g., Organic / Sugar Free"
                      />
                    </div>

                    <div className="inv-itemadd-field">
                      <label>Category (for this variant)</label>
                      <select
                        value={variantForm.category}
                        onChange={(e) => setVariantForm(vf => ({ ...vf, category: e.target.value }))}
                      >
                        <option value="">Select</option>
                        {VARIANT_CATEGORY_GROUPS.map(grp => (
                          <optgroup key={grp.label} label={grp.label}>
                            {grp.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                          </optgroup>
                        ))}
                      </select>
                    </div>

                    <div className="inv-itemadd-field" style={{ display: "flex", gap: 8, alignItems: "end" }}>
                      {!variantForm.variantId ? (
                        <button type="button" className="inv-itemadd-cta" onClick={createVariant}>
                          <PlusCircle size={16} /> Create
                        </button>
                      ) : (
                        <button type="button" className="inv-itemadd-cta" onClick={updateVariant} style={{ background: "#2563eb" }}>
                          <CheckCircle2 size={16} /> Update
                        </button>
                      )}
                      {variantForm.variantId ? (
                        <button
                          type="button"
                          className="inv-itemadd-outline"
                          onClick={() => setVariantForm({ variantId: "", variantTitle: "", category: form.category || "" })}
                        >
                          <X size={16} /> Cancel
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>Existing Variants</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {loadingVariants ? (
                        <div className="text-muted">Loading…</div>
                      ) : variants.length === 0 ? (
                        <div className="text-muted">No variants yet.</div>
                      ) : (
                        variants.map(v => (
                          <div
                            key={v.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "8px 10px",
                              border: "1px solid #e5e7eb",
                              borderRadius: 10,
                              background: "#fff",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontWeight: 600 }}>{v.variant_title}</span>
                              {v.category ? (
                                <span
                                  style={{
                                    fontSize: 12,
                                    padding: "2px 8px",
                                    borderRadius: 999,
                                    background: "rgba(37,99,235,0.1)",
                                    color: "#2563eb",
                                  }}
                                >
                                  {v.category}
                                </span>
                              ) : null}
                            </div>
                            <div style={{ display: "flex", gap: 6 }}>
                              <button
                                type="button"
                                className="inv-itemadd-icon-btn"
                                title="Edit"
                                onClick={() => startEditVariant(v)}
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                type="button"
                                className="inv-itemadd-icon-btn danger"
                                title="Delete"
                                onClick={() => deleteVariant(v.id)}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* DIET TAG */}
          <section className="inv-itemadd-section">
            <div className="inv-itemadd-section-title">
              <Beef size={16} />
              <span>Diet Tag</span>
            </div>
            <div className="inv-itemadd-grid-3">
              <div className="inv-itemadd-field">
                <label>Dish Type</label>
                <select name="dish" value={form.dish} onChange={handleChange}>
                  <option value="">None</option>
                  <option value="Veg">Veg</option>
                  <option value="Non-Veg">Non-Veg</option>
                  <option value="Egg">Egg</option>
                </select>
              </div>
              <div className="inv-itemadd-field">
                <label>Icon (name/url)</label>
                <input type="text" name="iconDish" value={form.iconDish} onChange={handleChange} placeholder="e.g., veg, nonveg, egg, or URL" />
              </div>
            </div>
          </section>

          {/* MEDIA */}
          <section className="inv-itemadd-section">
            <div className="inv-itemadd-section-title">
              <ImageIcon size={16} />
              <span>Media</span>
            </div>
            <div className="inv-itemadd-grid-2">
              <div className="inv-itemadd-field">
                <label>Photo URL</label>
                <input
                  type="text"
                  name="photo"
                  value={form.photo && !form.photo.startsWith('data:') ? form.photo : ''}
                  onChange={(e) => { setForm(f => ({ ...f, photo: e.target.value })); setPreview(''); }}
                />
              </div>
              <div className="inv-itemadd-field">
                <label>Upload Image</label>
                <input type="file" accept="image/*" onChange={(e) => handleFileToBase64(e.target.files?.[0])} />
              </div>
            </div>
            {preview && <div className="inv-itemadd-preview"><img src={preview} alt="preview" /></div>}
          </section>

          {/* PRICING */}
          <section className="inv-itemadd-section">
            <div className="inv-itemadd-section-title">
              <DollarSign size={16} />
              <span>Pricing</span>
            </div>
            <div className="inv-itemadd-grid-3">
              <div className="inv-itemadd-field">
                <label>Price<span className="inv-itemadd-req">*</span></label>
                <input type="number" name="price" value={form.price} onChange={handleChange} min="0" step="0.01" required />
              </div>
              <div className="inv-itemadd-field">
                <label>Discount %</label>
                <input type="number" name="discount" value={form.discount} onChange={(e) => { setManualDiscountPrice(false); handleChange(e); }} min="0" max="100" step="0.01" />
              </div>
              <div className="inv-itemadd-field">
                <label>Discounted Price</label>
                <input type="number" name="discountPrice" value={form.discountPrice} onChange={handleDiscountPriceChange} min="0" step="0.01" />
              </div>
              <div className="inv-itemadd-field">
                <label>Ratings (0-5)</label>
                <input type="number" name="ratings" value={form.ratings} onChange={handleChange} min="0" max="5" step="0.1" />
              </div>
            </div>
          </section>

          {/* FOOTER */}
          <div className="inventory-modal-footer inv-itemadd-footer-sticky">
            <button type="button" className="inv-itemadd-outline" onClick={onClose} disabled={submitting}>
              <X size={16} /> Cancel
            </button>
            <button type="submit" className="inv-itemadd-cta" disabled={submitting}>
              <CheckCircle2 size={16} /> {submitting ? (isEdit ? 'Updating…' : 'Saving…') : (isEdit ? 'Update Item' : 'Save Item')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddItemModal;
